/**
 * Account-page tech stack: vendor collapse, ordering, and display.
 * GET /api/market/accounts/:id/tech-stack already owns the category set.
 */

import { isBibliothecaFamilyTenant } from '@/lib/tenantUtils';

export const VENDOR_PREVIEW_LIMIT = 3;

const TILE_HEADINGS = {
  library_systems: 'Library & Discovery',
  library: 'Library & Discovery',
  library_self_service: 'Library Self-Service',
  digital_course_content: 'Digital Course Content',
  reading_literacy: 'Reading & Literacy',
  reading_literacy_platforms: 'Reading & Literacy',
  lms: 'LMS',
  sis: 'SIS',
};

const GENERIC_VENDOR_KEYS = new Set([
  'builtinhouse', 'inhouse', 'internal', 'homegrown', 'none', 'na',
  'nosolutioninplace', 'lmsnative', 'local', 'custom',
]);

const STATE_SHORT_LABEL = {
  'Built in-house': 'In-house',
  'No solution in place': 'None',
};

const LEGAL_SUFFIXES = new Set([
  'ab', 'ag', 'bv', 'co', 'company', 'corp', 'corporation', 'gmbh', 'inc',
  'incorporated', 'kg', 'limited', 'llc', 'llp', 'lp', 'ltd', 'nv', 'oy',
  'plc', 'pty', 'pvt', 'sa', 'sarl', 'sas', 'se', 'spa', 'srl',
]);

const GENERIC_TRAILING = new Set([
  'group', 'holdings', 'media', 'service', 'services', 'software',
  'solution', 'solutions', 'system', 'systems', 'technologies', 'technology',
]);

const NON_COMMERCIAL_STATES = new Set(['internal_homegrown', 'lms_native', 'no_solution']);

const RELATIONSHIP_RANK = { ours: 0, competitor: 1, adjacent: 2 };

/** Lowercase, strip punctuation and legal / generic suffixes. */
export function normalizeVendorKey(name) {
  if (!name) return '';
  const tokens = String(name)
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[^\p{L}\p{N}]+/gu, ' ')
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .filter((token) => !LEGAL_SUFFIXES.has(token));
  while (tokens.length > 1 && GENERIC_TRAILING.has(tokens[tokens.length - 1])) {
    tokens.pop();
  }
  return tokens.join('');
}

function vendorIdentity(vendor) {
  return vendor?.vendor || vendor?.state_label || vendor?.product || '';
}

function isGenericVendorKey(key) {
  return !key || GENERIC_VENDOR_KEYS.has(key);
}

function keysMatch(a, b) {
  if (!a || !b) return false;
  if (a === b) return true;
  const [shorter, longer] = a.length <= b.length ? [a, b] : [b, a];
  return shorter.length >= 5 && longer.endsWith(shorter);
}

function isRicher(candidate, current) {
  const cProduct = candidate.product ? 1 : 0;
  const nProduct = current.product ? 1 : 0;
  if (cProduct !== nProduct) return cProduct > nProduct;
  const cSentiment = candidate.sentiment ? 1 : 0;
  const nSentiment = current.sentiment ? 1 : 0;
  if (cSentiment !== nSentiment) return cSentiment > nSentiment;
  const cAsOf = candidate.as_of ? 1 : 0;
  const nAsOf = current.as_of ? 1 : 0;
  return cAsOf > nAsOf;
}

function uniqueUrls(vendors) {
  const seen = new Set();
  const urls = [];
  vendors.forEach((vendor) => {
    const extra = Array.isArray(vendor.source_urls) ? vendor.source_urls : [];
    [vendor.source_url, ...extra].forEach((url) => {
      const trimmed = typeof url === 'string' ? url.trim() : '';
      if (!trimmed || seen.has(trimmed)) return;
      seen.add(trimmed);
      urls.push(trimmed);
    });
  });
  return urls;
}

function mergeVendorGroup(group) {
  let winner = group[0];
  group.forEach((vendor) => {
    if (isRicher(vendor, winner)) winner = vendor;
  });
  const sourceUrls = uniqueUrls(group);
  return {
    ...winner,
    source_url: sourceUrls[0] || winner.source_url || null,
    source_urls: sourceUrls,
  };
}

/**
 * Collapse duplicate vendor spellings (OverDrive / OverDrive, Inc.).
 * Keeps the richest row and unions source URLs.
 */
export function dedupeVendors(vendors) {
  const list = (vendors || []).filter(Boolean);
  if (list.length <= 1) {
    return list.map((vendor) => mergeVendorGroup([vendor]));
  }

  const keys = list.map((vendor) => normalizeVendorKey(vendorIdentity(vendor)));
  const productKeys = list.map((vendor) => normalizeVendorKey(vendor.product || ''));
  const parent = list.map((_, index) => index);

  const find = (index) => {
    if (parent[index] !== index) parent[index] = find(parent[index]);
    return parent[index];
  };
  const union = (i, j) => {
    const a = find(i);
    const b = find(j);
    if (a !== b) parent[b] = a;
  };
  const sameRow = (i, j) => {
    if (productKeys[i] && productKeys[i] === productKeys[j]) return true;
    if (isGenericVendorKey(keys[i]) || isGenericVendorKey(keys[j])) return false;
    return keysMatch(keys[i], keys[j]);
  };

  for (let i = 0; i < list.length; i += 1) {
    for (let j = i + 1; j < list.length; j += 1) {
      if (sameRow(i, j)) union(i, j);
    }
  }

  const groups = new Map();
  list.forEach((vendor, index) => {
    const root = find(index);
    if (!groups.has(root)) groups.set(root, []);
    groups.get(root).push(vendor);
  });

  return [...groups.values()].map(mergeVendorGroup);
}

export function sortVendors(vendors) {
  return [...(vendors || [])].sort((a, b) => {
    const relA = RELATIONSHIP_RANK[a?.relationship] ?? 3;
    const relB = RELATIONSHIP_RANK[b?.relationship] ?? 3;
    if (relA !== relB) return relA - relB;
    const commercialA = a?.state === 'commercial' ? 0 : 1;
    const commercialB = b?.state === 'commercial' ? 0 : 1;
    return commercialA - commercialB;
  });
}

export function prepareCategoryVendors(vendors) {
  return sortVendors(dedupeVendors(vendors));
}

/**
 * Lens categories lead; context stays in API order within each group.
 * Unknown / empty categories are not invented here.
 */
export function partitionStackByRole(stack) {
  const lens = [];
  const context = [];
  (stack || []).forEach((category) => {
    if (category?.role === 'context') context.push(category);
    else lens.push(category);
  });
  return { lens, context };
}

function titleCaseHeading(raw) {
  return String(raw || '')
    .replace(/_/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .split(' ')
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

export function categoryHeading(category, tenantId) {
  const key = String(category?.category || '').trim().toLowerCase().replace(/\s+/g, '_');
  if (isBibliothecaFamilyTenant(tenantId) && (key === 'library_systems' || key === 'library')) {
    return 'Library systems';
  }
  if (TILE_HEADINGS[key]) return TILE_HEADINGS[key];
  const label = typeof category?.label === 'string' ? category.label.trim() : '';
  if (!label) return null;
  const labelKey = label.toLowerCase().replace(/\s+/g, '_');
  if (TILE_HEADINGS[labelKey]) return TILE_HEADINGS[labelKey];
  if (label.includes('/')) return titleCaseHeading(label.split('/')[0]);
  if (label.includes('_') || label === label.toLowerCase()) return titleCaseHeading(label);
  return label;
}

export function vendorPrimaryLabel(vendor) {
  if (!vendor) return null;
  if (NON_COMMERCIAL_STATES.has(vendor.state) && vendor.state_label) {
    return STATE_SHORT_LABEL[vendor.state_label] || vendor.state_label;
  }
  if (vendor.state === 'open_source') {
    return vendor.state_label || vendor.vendor || vendor.product || null;
  }
  return vendor.vendor || vendor.state_label || vendor.product || null;
}

export function vendorProductLabel(vendor) {
  if (!vendor) return null;
  const product = typeof vendor.product === 'string' ? vendor.product.trim() : '';
  const vendorName = typeof vendor.vendor === 'string' ? vendor.vendor.trim() : '';
  const genericVendor = !vendorName
    || GENERIC_VENDOR_KEYS.has(normalizeVendorKey(vendorName))
    || NON_COMMERCIAL_STATES.has(vendor.state);

  if (genericVendor) {
    return product || vendorPrimaryLabel(vendor);
  }
  if (product && vendorName) {
    if (product.toLowerCase().startsWith(vendorName.toLowerCase())) return product;
    if (product === vendorName) return product;
    return `${vendorName} · ${product}`;
  }
  return product || vendorName || vendorPrimaryLabel(vendor);
}
